/**
 * GRA8-ROLL-4 — MySQL data-type vocabulary.
 *
 * Surfaces the curated MySQL 8 type list at the "data-type" slot the
 * EditorContext slot seam classifies (CREATE PROCEDURE/FUNCTION parameter type
 * slot — the only mysql data-type slot wired this slice). The slot classifier
 * (`isMysqlDataTypeSlot` in `editorContextSlot.mysql.ts`) does the position
 * recognition; this module only owns the vocabulary. Unlike the pgsql / oracle
 * data-type modules there is deliberately NO `isMysqlDataTypePosition`
 * text-walk here — classification is seam-only, dispatched on
 * `positionKind === 'data-type'` from the resolver.
 *
 * Mirrors `pgsqlDataTypes.ts` / `oracleDataTypes.ts` shape.
 */

/**
 * Curated MySQL 8 data type vocabulary surfaced at the parameter-type slot.
 * Covers the numeric, string, temporal, binary, blob/text, structured (JSON /
 * ENUM / SET), and spatial categories from the MySQL catalog.
 */
const MYSQL_DATA_TYPES: readonly string[] = [
  // Numeric
  'TINYINT',
  'SMALLINT',
  'MEDIUMINT',
  'INT',
  'INTEGER',
  'BIGINT',
  'DECIMAL',
  'NUMERIC',
  'FLOAT',
  'DOUBLE',
  'BIT',
  'BOOLEAN',
  // Date / time
  'DATE',
  'DATETIME',
  'TIMESTAMP',
  'TIME',
  'YEAR',
  // String
  'CHAR',
  'VARCHAR',
  'BINARY',
  'VARBINARY',
  // Blob / text
  'TINYBLOB',
  'BLOB',
  'MEDIUMBLOB',
  'LONGBLOB',
  'TINYTEXT',
  'TEXT',
  'MEDIUMTEXT',
  'LONGTEXT',
  // Structured
  'ENUM',
  'SET',
  'JSON',
  // Spatial
  'GEOMETRY',
  'POINT',
  'LINESTRING',
  'POLYGON',
];

/**
 * The types MySQL accepts as a `CAST(x AS ▮)` / `CONVERT(x, ▮)` target — a
 * different, smaller vocabulary than the column-declaration list above.
 * `CAST(x AS INT)` and `CAST(x AS VARCHAR)` are syntax errors in MySQL; the
 * integer targets are spelled `SIGNED` / `UNSIGNED`, and the string target is
 * `CHAR`. Emitting the declaration list here offered types that cannot be
 * used and hid the two that are.
 */
const MYSQL_CAST_TARGET_TYPES: readonly string[] = [
  'SIGNED',
  'SIGNED INTEGER',
  'UNSIGNED',
  'UNSIGNED INTEGER',
  'CHAR',
  'NCHAR',
  'BINARY',
  'DECIMAL',
  'DOUBLE',
  'FLOAT',
  'REAL',
  'DATE',
  'DATETIME',
  'TIME',
  'YEAR',
  'JSON',
  // Spatial targets, valid since MySQL 8.0.24.
  'POINT',
  'LINESTRING',
  'POLYGON',
  'MULTIPOINT',
  'MULTILINESTRING',
  'MULTIPOLYGON',
  'GEOMETRYCOLLECTION',
];

export const MYSQL_DATA_TYPES_LIST = MYSQL_DATA_TYPES;

export function getMysqlCastTargetTypes(): readonly string[] {
  return MYSQL_CAST_TARGET_TYPES;
}

export function getMysqlDataTypes(): readonly string[] {
  return MYSQL_DATA_TYPES;
}
